import { useQuery } from '@tanstack/react-query'
import { useParams, Link, useNavigate, useLocation } from 'react-router-dom'
import { useEffect } from 'react'
import ReactMarkdown from 'react-markdown'
import { ProductArticle } from '../components/articles/ProductArticle'
import { BatProfileBars } from '../components/BatProfileBars'
import { PageContextBar } from '../components/navigation/PageContextBar'
import { SectionNav } from '../components/navigation/SectionNav'
import { getProduct, getRelatedProducts } from '../services/api'
import { getImageUrl } from '../utils/imageUrl'
import { parseProductIdFromSlug, productPath } from '../utils/productUrl'
import { normalizeArticleMarkdown } from '../utils/articleMarkdown'
import { cleanProductLabel } from '../utils/displayText'
import { structureProductArticle } from '../utils/articleStructure'
import './ProductPage.css'

function RelatedCard({ product }) {
  return (
    <Link to={productPath(product)} className="related-card">
      <div className="related-card-image">
        {product.primary_image_url ? (
          <img src={getImageUrl(product.primary_image_url)} alt={product.name} />
        ) : (
          <div className="related-card-placeholder">
            <span>Intet billede</span>
          </div>
        )}
      </div>
      <div className="related-card-info">
        <span className="related-card-brand">{product.brand}</span>
        <h4>{product.model}</h4>
        {product.year && <span className="related-card-year">{product.year}</span>}
      </div>
    </Link>
  )
}

function ProductPage() {
  const { slug } = useParams()
  const id = parseProductIdFromSlug(slug)
  const navigate = useNavigate()
  const location = useLocation()
  const { data: product, isLoading, error } = useQuery({
    queryKey: ['product', id],
    queryFn: () => getProduct(id),
    retry: 2,
    retryDelay: 1000,
  })

  const { data: related } = useQuery({
    queryKey: ['related', id],
    queryFn: () => getRelatedProducts(id),
    enabled: !!product,
  })

  const canonicalPath = product ? (product.url_path || productPath(product)) : null
  useEffect(() => {
    if (!canonicalPath || location.pathname === canonicalPath) return
    navigate(canonicalPath, { replace: true })
  }, [canonicalPath, location.pathname, navigate])

  if (isLoading) {
    return (
      <div className="page-shell">
        <div className="page-loading">Indlæser produkt...</div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="page-shell">
        <div className="page-error">
          <p><strong>Fejl ved indlæsning</strong></p>
          <p>{error.message}</p>
        </div>
      </div>
    )
  }

  if (!product) {
    return (
      <div className="page-shell">
        <div className="page-empty">Produkt ikke fundet</div>
      </div>
    )
  }

  const isPadelBat = product.category === 'padel_bat'
  const shortName = cleanProductLabel(product.model || product.name)
  const article = structureProductArticle({
    description: product.description,
    summary: product.summary,
  })
  const hasArticle = product.description && article.hasContent

  const pageSections = [{ id: 'produkt-oversigt', label: 'Oversigt' }]
  if (hasArticle) {
    article.sections.forEach(({ id, label }) => {
      pageSections.push({ id, label })
    })
  } else if (product.description) {
    pageSections.push({ id: 'produkt-artikel', label: 'Beskrivelse' })
  }
  if (product.reviews?.some((r) => (r.key_points || []).length)) {
    pageSections.push({ id: 'produkt-eksperter', label: 'Eksperter' })
  }
  if (product.reviews?.length) {
    pageSections.push({ id: 'produkt-anmeldelser', label: 'Anmeldelser' })
  }
  if (related && (
    related.same_brand_more_aggressive?.length > 0 ||
    related.same_brand_more_control?.length > 0 ||
    related.similar_alternatives?.length > 0
  )) {
    pageSections.push({ id: 'produkt-lignende', label: 'Lignende' })
  }

  const contextActions = []
  if (isPadelBat) {
    if (product.brand) {
      contextActions.push({
        label: `Alle ${product.brand} bats`,
        to: `/bat?brand=${encodeURIComponent(product.brand)}`,
        primary: true,
      })
    }
    contextActions.push({ label: 'Padel bats', to: '/bat' })
  }

  return (
    <div className="page-shell product-page">
      <PageContextBar
        backLabel="Tilbage"
        breadcrumbs={[
          { label: 'Hjem', to: '/' },
          ...(isPadelBat ? [{ label: 'Padel bats', to: '/bat' }] : []),
          { label: shortName },
        ]}
        actions={contextActions}
      />

      <SectionNav sections={pageSections} />

      <header id="produkt-oversigt" className="product-hero scroll-target">
        <div className="product-hero-image">
          {product.primary_image_url ? (
            <img
              src={getImageUrl(product.primary_image_url)}
              alt={product.name}
              className="product-main-image"
            />
          ) : (
            <div className="product-image-placeholder">
              <svg width="120" height="120" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                <circle cx="8.5" cy="8.5" r="1.5" />
                <polyline points="21 15 16 10 5 21" />
              </svg>
              <span>Intet billede tilgængeligt</span>
            </div>
          )}
        </div>
        <div className="product-hero-content">
          <p className="product-brand">{product.brand}</p>
          <h1 className="product-title">{cleanProductLabel(product.name)}</h1>
          <p className="product-meta">
            {cleanProductLabel(product.model)}
            {product.year && <span className="product-year"> • {product.year}</span>}
          </p>

          {isPadelBat && (
            <div className="product-hero-specs">
              <BatProfileBars
                specifications={product.specifications}
                profileScores={product.profile_scores}
                className="product-hero-profile"
              />
            </div>
          )}

          {product.summary && (
            <div className="product-summary">
              <div className="product-summary-formatted">
                <ReactMarkdown>{normalizeArticleMarkdown(product.summary)}</ReactMarkdown>
              </div>
            </div>
          )}
        </div>
      </header>

      {hasArticle ? (
        <section id="produkt-artikel" className="description-section scroll-target">
          <ProductArticle product={product} />
        </section>
      ) : product.description ? (
        <section id="produkt-artikel" className="description-section scroll-target">
          <h2>Beskrivelse</h2>
          <div className="description-formatted">
            <ReactMarkdown>{normalizeArticleMarkdown(product.description)}</ReactMarkdown>
          </div>
        </section>
      ) : null}

      {product.reviews && product.reviews.some((r) => (r.key_points || []).length) && (
        <section id="produkt-eksperter" className="expert-quotes-section scroll-target">
          <h2>Ekspert udtagelser</h2>
          <div className="expert-quotes-list">
            {product.reviews
              .filter((r) => (r.key_points || []).length)
              .flatMap((r) =>
                r.key_points.map((q, i) => (
                  <blockquote key={r.id + '-' + i} className="expert-quote">
                    <p>{q}</p>
                    <footer>
                      {r.author && <cite>{r.author}</cite>}
                      {r.source_url && <a href={r.source_url} target="_blank" rel="noopener noreferrer">YouTube</a>}
                    </footer>
                  </blockquote>
                ))
              )}
          </div>
        </section>
      )}
      {product.reviews && product.reviews.length > 0 && (
        <section id="produkt-anmeldelser" className="reviews-section scroll-target">
          <h2>Anmeldelser</h2>
          <div className="reviews-list">
            {product.reviews.map((review) => (
              <article key={review.id} className="review-card">
                {review.rating && (
                  <div className="review-rating">
                    {'⭐'.repeat(Math.round(review.rating / 2))} {review.rating}/10
                  </div>
                )}
                {review.summary && <p className="review-summary">{review.summary}</p>}
                {review.content && <p className="review-content">{review.content}</p>}
                {review.author && <footer className="review-author">— {review.author}</footer>}
              </article>
            ))}
          </div>
        </section>
      )}

      {related && (related.same_brand_more_aggressive?.length > 0 || related.same_brand_more_control?.length > 0 || related.similar_alternatives?.length > 0) && (
        <section id="produkt-lignende" className="related-section scroll-target">
          <h2>Lignende produkter</h2>
          <div className="related-grid">
            {related.same_brand_more_aggressive?.length > 0 && (
              <div className="related-group">
                <h3>En tand mere aggressiv <span className="related-sub">(samme mærke)</span></h3>
                <div className="related-cards">
                  {related.same_brand_more_aggressive.map((p) => (
                    <RelatedCard key={p.id} product={p} />
                  ))}
                </div>
              </div>
            )}
            {related.same_brand_more_control?.length > 0 && (
              <div className="related-group">
                <h3>En tand mere kontrol <span className="related-sub">(samme mærke)</span></h3>
                <div className="related-cards">
                  {related.same_brand_more_control.map((p) => (
                    <RelatedCard key={p.id} product={p} />
                  ))}
                </div>
              </div>
            )}
            {related.similar_alternatives?.length > 0 && (
              <div className="related-group">
                <h3>Lignende alternativer <span className="related-sub">(andre mærker)</span></h3>
                <div className="related-cards">
                  {related.similar_alternatives.map((p) => (
                    <RelatedCard key={p.id} product={p} />
                  ))}
                </div>
              </div>
            )}
          </div>
        </section>
      )}
    </div>
  )
}

export default ProductPage
